import type { Rotation } from "@/lib/protocol";
import type { TotalInk } from "@/lib/total-ink";
import type { PrintChecksReport } from "@/lib/print-checks";

export type Segment = { text: string; strong?: true };

export type Checks = {
  status: "pending" | "running" | "complete" | "warning" | "unavailable";
  phase: string | null;
  page: number | null;
  pageCount: number;
  warning?: string | null;
};

export function checksActive(checks: Checks | undefined): boolean {
  return checks?.status === "pending" || checks?.status === "running";
}

export function checksTerminal(checks: Checks | undefined): boolean {
  return !checks || !checksActive(checks);
}

export function mayShowDetailedWarning(checks: Checks | undefined): boolean {
  return checks !== undefined && Object.hasOwn(checks, "warning");
}

export function checksSentence(checks: Checks | undefined, uploaderName: string | null | undefined): string | null {
  if (!checks || checks.status === "complete") return null;
  if (checks.status === "pending") return "The proof is open. Print checks running.";
  if (checks.status === "running") return `Print checks running${checks.page === null ? "" : `, page ${checks.page} of ${checks.pageCount}`}. Nothing here waits on them.`;
  if (checks.status === "unavailable") return "Print checks could not finish. The proof stays open, nothing here is blocked.";
  if (!mayShowDetailedWarning(checks)) return "Print checks found a problem. The proof stays open, nothing here is blocked.";
  return uploaderName ? `Print checks found a problem. The proof stays open and every comment is kept. ${uploaderName}, please fix it in the next version.` : "Print checks found a problem. The proof stays open and every comment is kept. Please fix it in the next version.";
}

/* The drawing bolds only the "Print checks ..." sentence (mock/pictures-first.html:301,
 * :450), so a status line renders as [before, bold lead, after]. */
export function checksSentenceParts(text: string): [string, string, string] {
  const start = Math.max(0, text.indexOf("Print checks"));
  const end = text.indexOf(".", start) + 1 || text.length;
  return [text.slice(0, start), text.slice(start, end), text.slice(end)];
}

type Check = { pass: number; fail: number; unavailable: number };

export type UploadReport = {
  version: {
    id: string;
    n: number;
    gen: number;
    workspaceId: string;
    issueNumber: number;
    filename: string | null;
    pageCount: number;
    byteSize: number;
    sourceByteSize: number | null;
    uploadedAt: string;
    uploadedBy: { id: string; name: string };
    archivedAt: string | null;
    live: boolean;
    repairStatus: "clean" | "repaired" | null;
    storedFileKind?: "candidate" | "source" | null;
    repairAcknowledgedAt: string | null;
    repairAcknowledgedBy: { id: string; name: string } | null;
  };
  repair: { warningCount: number | null; staleHints: boolean; damaged: boolean; repairMs: number | null; spreads?: { pagesBefore: number; pagesAfter: number; spreadPages: number[] } | null };
  proof: {
    pagesChecked: number | null;
    checks: Record<"structure" | "text" | "raster" | "print_settings" | "ink_corroboration", Check> | null;
    drawErrorPagesBeforeRepair: number[] | null;
    drawErrorPagesAfterRepair: number[] | null;
    tools: { qpdf: string | null; poppler: string | null; ghostscript: string | null } | null;
    freeObjects: { count: number; first: number; last: number } | null;
    annotationObjects: boolean;
    creator: string | null;
    producer: string | null;
  };
  totalInk: TotalInk | null;
  preflight: PrintChecksReport | null;
  pages: { n: number; wPt: number; hPt: number; rotation: Rotation }[];
  mayAct: boolean;
  checks?: Checks;
};

const requiredChecks = ["structure", "text", "raster", "print_settings"] as const;
const allChecks = [...requiredChecks, "ink_corroboration"] as const;

/* WHAT THE PROOF ACTUALLY SAID, in one place.
 *
 * Three sentences on this screen used to assert a clean proof without looking
 * at the data: noPageChanged() printed "No page changed" whatever the tallies
 * were, proofVerdict() printed "5 checks, every page passed" when there were no
 * tallies at all, and whatHappenedVerdict() printed "No page changed" beside
 * both. That is the same defect class as the "Last here: never signed in" line
 * that had to be pulled: a screen stating something the data does not support.
 *
 * So the outcome is read once, here, and the three sentences below all branch on
 * it, which is also what stops them ever disagreeing with each other again.
 *
 *   pass        every required check compared every page the proof covered and
 *               found no difference. This is the only state the mock draws
 *               (repair-report.html:267, :280), because on the owner's own file
 *               every page passed.
 *   partial     the proof ran and found no difference, but it did not cover
 *               every page, or it did not record how many pages it covered.
 *   fail        a check recorded a difference on at least one page.
 *   unrecorded  there is no result to report: no tallies at all, or a required
 *               check produced neither a pass nor a failure.
 *
 * The wording for the three states the mock does not draw follows the pattern
 * the screen already uses for an unrecorded measurement, repair-report.html's
 * fourth sentence as built ("... was not recorded for this upload"): plain,
 * complete sentences, no jargon, and never a verdict the tallies do not carry. */
export type ProofOutcome = "pass" | "partial" | "fail" | "unrecorded";

/** Pages that passed EVERY required check, which is the number the mock's
 *  "52 of 52 pages passed on every one" (repair-report.html:280) reports. */
function pagesPassed(checks: NonNullable<UploadReport["proof"]["checks"]>): number {
  return Math.min(...requiredChecks.map((key) => checks[key]?.pass ?? 0));
}

function failedChecks(checks: NonNullable<UploadReport["proof"]["checks"]>): readonly (typeof allChecks)[number][] {
  return allChecks.filter((key) => (checks[key]?.fail ?? 0) > 0);
}

export function proofOutcome(r: UploadReport): ProofOutcome {
  const checks = r.proof.checks;
  if (!checks) return "unrecorded";
  if (failedChecks(checks).length > 0) return "fail";
  if (requiredChecks.some((key) => (checks[key]?.pass ?? 0) === 0)) return "unrecorded";
  const pages = r.proof.pagesChecked;
  return pages !== null && pagesPassed(checks) === pages ? "pass" : "partial";
}

/** The check's name as the proof rows print it (repair-report.html:282-286), so
 *  a sentence about a check and its own row say the same words. */
function checkName(key: string): string {
  return rowDetails.find(([rowKey]) => rowKey === key)?.[1] ?? key;
}

function sentenceList(parts: string[]): string {
  if (parts.length <= 1) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;
}

function pageWord(n: number): string {
  return n === 1 ? "1 page" : `${n} pages`;
}

export function bytes(n: number): string {
  return n.toLocaleString("en-US");
}

export function repairSeconds(ms: number): string {
  return (ms / 1000).toFixed(2);
}

function pagesList(pages: number[]): string {
  if (pages.length === 1) return String(pages[0]);
  if (pages.every((page, index) => index === 0 || page === pages[index - 1]! + 1)) return `${pages[0]} to ${pages.at(-1)}`;
  if (pages.length === 2) return `${pages[0]} and ${pages[1]}`;
  return `${pages.slice(0, -1).join(", ")} and ${pages.at(-1)}`;
}

export function spreadsSentence(r: UploadReport, noun = "issue"): Segment[] | null {
  const spreads = r.repair.spreads;
  if (!spreads || spreads.pagesAfter !== spreads.pagesBefore + spreads.spreadPages.length || spreads.spreadPages.length === 0) return null;
  const pages = spreads.spreadPages;
  const label = pages.length === 1 ? "page" : "pages";
  return [{ text: `This file came in as spreads. Studio split ${label} ${pagesList(pages)} into ${pages.length === 1 ? "two single pages" : "single pages"}, so the ${noun} now has ${spreads.pagesAfter} pages.` }];
}

function warningText(count: number): string {
  return `${count} structural warning${count === 1 ? "" : "s"}`;
}

export function whatWasWrong(r: UploadReport): { sentence: Segment[]; sub: string | null } {
  const free = r.proof.freeObjects;
  /* `?? 0` was here, which read an unrecorded warning count as a measured zero
   * and ended on "no structural warnings" below: an absence of data printed as
   * a clean result. Null now falls through to its own sentence. */
  const warningCount = r.repair.warningCount;
  const sub = warningCount !== null && warningCount > 0 ? `qpdf reported ${warningCount} warning${warningCount === 1 ? "" : "s"} and no errors on the file as supplied.` : null;
  if (free && free.count > 1) {
    // mock repair-report.html:264 reads "Objects 2374 to 2404 ... were marked
    // free yet still referenced", naming the range only, not a separate count.
    const annotation = r.proof.annotationObjects ? ", which are annotation objects," : ",";
    return {
      sentence: [
        { text: "The file as supplied had a " },
        { text: "damaged cross-reference table", strong: true },
        { text: `. Objects ${free.first} to ${free.last}${annotation} were marked free yet still referenced.` },
      ],
      sub,
    };
  }
  if (free && free.count === 1) {
    const annotation = r.proof.annotationObjects ? ", an annotation object," : "";
    return {
      sentence: [
        { text: "The file as supplied had a " },
        { text: "damaged cross-reference table", strong: true },
        { text: `. Object ${free.first}${annotation} was marked free yet still referenced.` },
      ],
      sub,
    };
  }
  if (r.repair.damaged) return { sentence: [{ text: "The file as supplied had a " }, { text: "damaged cross-reference table", strong: true }, { text: "." }], sub };
  if (r.repair.staleHints) return { sentence: [{ text: "The file as supplied carried " }, { text: "out of date linearisation hints", strong: true }, { text: "." }], sub };
  if (warningCount !== null && warningCount > 0) return { sentence: [{ text: "The file as supplied had " }, { text: warningText(warningCount), strong: true }, { text: "." }], sub };
  // mock repair-report.html:439 names the clean upload's report as the same four
  // slots with "no structural warnings" in the first. That is a MEASURED zero.
  if (warningCount === 0) return { sentence: [{ text: "The file as supplied had " }, { text: "no structural warnings", strong: true }, { text: "." }], sub };
  return { sentence: [{ text: "What was wrong with the file as supplied was not recorded for this upload." }], sub };
}

export function whatWasRebuilt(r: UploadReport): Segment[] {
  const status = r.version.repairStatus;
  /* A null status is not a clean one: with no recorded status, "rewritten by
   * qpdf" is a claim about work nobody wrote down. The byte counts are a
   * separate measurement and are still reported when they exist. */
  const sentence: Segment[] = status === "repaired"
    ? [{ text: "The " }, { text: "cross-reference table and object streams were rebuilt", strong: true }, { text: ". Free objects and linearisation hints were dropped." }]
    : status === "clean"
      ? [{ text: "The file was " }, { text: "rewritten by qpdf", strong: true }, { text: "." }]
      : [{ text: "What was rebuilt was not recorded for this upload." }];
  if (r.version.sourceByteSize !== null) sentence.push({ text: ` ${bytes(r.version.sourceByteSize)} bytes became ${bytes(r.version.byteSize)} bytes.` });
  return sentence;
}

/* Sentence 3. The mock draws the pass case only, verbatim below
 * (repair-report.html:267); every other outcome says what the tallies say and
 * never that the result was clean. `pagesChecked ?? version.pageCount` used to
 * stand in here too, which turned "the proof did not record its coverage" into
 * "all 52 pages were checked". */
export function noPageChanged(r: UploadReport): Segment[] {
  const checks = r.proof.checks;
  const pages = r.proof.pagesChecked;
  if (!checks) return [{ text: "Whether any page changed was not recorded for this upload." }];

  const failed = failedChecks(checks);
  if (failed.length > 0) {
    const clauses = failed.map((key) => `${checkName(key)} recorded a difference on ${pageWord(checks[key]!.fail)}.`);
    return [{ text: "Not every page is identical.", strong: true }, { text: ` ${clauses.join(" ")}` }];
  }

  const noResult = requiredChecks.filter((key) => (checks[key]?.pass ?? 0) === 0);
  if (noResult.length > 0) {
    return [{ text: `Whether any page changed is not settled. ${sentenceList(noResult.map(checkName))} recorded no result.` }];
  }

  const passed = pagesPassed(checks);
  if (pages === null) {
    return [{ text: `${pageWord(passed)} ${passed === 1 ? "was" : "were"} compared and ${passed === 1 ? "is" : "are"} identical in text, layout, appearance and print settings. How many pages the proof covered was not recorded.` }];
  }
  if (passed < pages) {
    return [{ text: `${passed} of the ${pages} pages checked are identical in text, layout, appearance and print settings. The other ${pages - passed} could not be compared.` }];
  }
  return pages === 1
    ? [{ text: "No page changed.", strong: true }, { text: " The page was checked and is identical in text, layout, appearance and print settings." }]
    : [{ text: "No page changed.", strong: true }, { text: ` All ${pages} pages were checked and are identical in text, layout, appearance and print settings.` }];
}

export function wouldNotRender(r: UploadReport): { sentence: Segment[]; sub: string | null } {
  const pages = r.proof.drawErrorPagesBeforeRepair;
  if (pages === null) return { sentence: [{ text: "Which pages would not have rendered without the repair was not recorded for this upload." }], sub: null };
  if (pages.length === 0) return { sentence: [{ text: "Every page would have rendered without the repair." }], sub: null };
  if (pages.length === 1) {
    return {
      sentence: [{ text: "Without the repair, " }, { text: "1 page would not have rendered correctly", strong: true }, { text: ": page " }, { text: String(pages[0]), strong: true }, { text: "." }],
      sub: drawSub("Ghostscript could not draw it from the file as supplied.", r),
    };
  }
  const beforeLast = pages.slice(0, -1).join(", ");
  return {
    sentence: [{ text: "Without the repair, " }, { text: `${pages.length} pages would not have rendered correctly`, strong: true }, { text: ": pages " }, { text: beforeLast, strong: true }, { text: " and " }, { text: String(pages.at(-1)), strong: true }, { text: "." }],
    sub: drawSub("Ghostscript could not draw them from the file as supplied.", r),
  };
}

function drawSub(sub: string, r: UploadReport): string {
  const after = r.proof.drawErrorPagesAfterRepair;
  if (after === null) return sub;
  const pages = r.proof.pagesChecked ?? r.version.pageCount;
  if (after.length === 0) return sub + (pages === 1 ? " It draws the page of the repaired file." : ` It draws all ${pages} pages of the repaired file.`);
  if (after.length === 1) return `${sub} It still could not draw page ${after[0]} of the repaired file.`;
  return `${sub} It still could not draw pages ${after.slice(0, -1).join(", ")} and ${after.at(-1)} of the repaired file.`;
}

export function toolsLine(r: UploadReport): string | null {
  const tools = r.proof.tools;
  if (tools === null) return null;
  const line = [
    tools.qpdf === null ? null : `qpdf ${tools.qpdf}`,
    tools.poppler === null ? null : `poppler ${tools.poppler}`,
    tools.ghostscript === null ? null : `ghostscript ${tools.ghostscript}`,
  ].filter((item): item is string => item !== null);
  return line.length ? line.join(" · ") + " · one recorded comparison profile" : null;
}

// mock preflight.html:257-258, section 1's summary-line verdict ("Repaired and
// proved. No page changed."), reused as the fold's .sv text for the real
// section (fix 5). The "Repaired" half is the recorded repair status; the rest
// is the proof outcome, so a folded section cannot claim a clean proof the
// tallies do not carry. It said "No page changed" unconditionally before.
export function whatHappenedVerdict(r: UploadReport): string {
  const repaired = r.version.repairStatus === "repaired";
  const outcome = proofOutcome(r);
  if (outcome === "pass") return repaired ? "Repaired and proved. No page changed." : "Proved. No page changed.";
  if (outcome === "fail") return repaired ? "Repaired. Not every page is identical." : "Not every page is identical.";
  if (outcome === "partial") return repaired ? "Repaired. Some pages could not be compared." : "Some pages could not be compared.";
  return repaired ? "Repaired. The proof has no recorded result." : "The proof has no recorded result.";
}

// The count on section 2's summary line, mock repair-report.html:280
// ("5 checks, 52 of 52 pages passed on every one"). With no tallies it returned
// that same "every page passed" verdict, which is a claim about a proof whose
// result is not on the report at all.
export function proofVerdict(r: UploadReport): string {
  const checks = r.proof.checks;
  if (!checks) return "5 checks, no result recorded";
  const failed = failedChecks(checks);
  if (failed.length > 0) return `5 checks, ${failed.length} recorded a difference`;
  const noResult = requiredChecks.filter((key) => (checks[key]?.pass ?? 0) === 0);
  if (noResult.length > 0) return `5 checks, ${noResult.length} with no result`;
  const passed = pagesPassed(checks);
  const pages = r.proof.pagesChecked;
  return pages === null ? `5 checks, ${pageWord(passed)} passed on every one` : `5 checks, ${passed} of ${pages} pages passed on every one`;
}

const rowDetails = [
  ["structure", "Page boxes and rotation", "Same page count and order. MediaBox, CropBox, TrimBox and BleedBox, present or absent alike, and the page rotation, read from both files and compared by value."],
  ["text", "Text and word geometry", "The page’s text and every word’s box, extracted from both files the same way, byte for byte."],
  ["raster", "Appearance at 150 dpi", "Each page drawn from both files at Studio’s reading tier, same renderer and flags, and the pixels compared. Dimensions are part of the comparison."],
  ["print_settings", "Print settings", "Output intent, the colour spaces and colourant names in each page’s resources, the PDF/X version, and the overprint flags, compared as sets of values."],
  ["ink_corroboration", "Ink coverage rows", "Ghostscript’s four coverage numbers per page at 72 dpi, from both files. Where Ghostscript cannot draw a page of the file as supplied, the row is recorded unavailable and the verdict rests on checks 1 to 4."],
] as const;

// mock repair-report.html:136-138 draws three states for a check's result,
// pass/fail/na, and the built screen only ever painted pass (fix 4). A check
// fails if any page failed it; with no failures but nothing passed either
// (the whole check unavailable) it reads na; otherwise pass, even when some
// pages are separately marked unavailable, since the caveat already says the
// verdict then rests on the other checks.
export type CheckStatus = "pass" | "fail" | "na";
function checkStatus(check: Check | undefined): CheckStatus {
  if (!check) return "na";
  if (check.fail > 0) return "fail";
  if (check.pass === 0) return "na";
  return "pass";
}

export function checkRows(r: UploadReport): { key: string; name: string; description: string; count: string; status: CheckStatus; corroborating: boolean }[] {
  /* The denominator is the pages the PROOF covered, mock repair-report.html:282
   * "52 / 52". It was `pagesChecked ?? version.pageCount`, which printed the
   * version's own page count as though the proof had covered it: a row reading
   * "50 / 52" when nothing recorded that 52 pages were ever compared. With no
   * recorded coverage the row now shows the pass count and no denominator. */
  const pages = r.proof.pagesChecked;
  return rowDetails.map(([key, name, description], index) => {
    const check = r.proof.checks?.[key];
    let count = "";
    if (check) {
      count = `${check.pass}${pages === null ? "" : ` / ${pages}`}${index === 4 ? " identical" : ""}`;
      if (check.unavailable > 0) count += `, ${check.unavailable} unavailable`;
    }
    return { key, name, description, count, status: checkStatus(check), corroborating: index === 4 };
  });
}

export function actionErrorText(code: string): string {
  if (code === "already_acknowledged") return "Someone has already acknowledged this repair.";
  if (code === "archived") return "This version is already archived.";
  if (code === "not_repaired") return "This version was not repaired, so there is nothing to acknowledge.";
  if (code === "forbidden") return "You do not have permission to do this.";
  return "That did not go through. Try it again.";
}
